import {
  DiffUnit,
  ExperimentCreateInput,
  Granularity,
  PausePeriodEntity,
  SegmentMetricsEntity,
  ExperimentEntity,
  ExperimentListItem,
  ExperimentResults,
  ExperimentStatus,
  MetricComparisonEntity,
  ScenarioMetricsEntity,
  TimeSeriesMetricValueEntity,
  TimeSeriesPointEntity,
  VariantEntity,
  VariantInput,
  VariantRole,
  VariantType,
  Verdict,
} from "../../domain/experiment.entity";
import {
  Experiment,
  ExperimentListItem as ExperimentListItemProto,
  GetExperimentResultsResponse,
  MetricComparison,
  PausePeriod,
  ScenarioMetrics,
  SegmentMetrics,
  TimeSeriesMetricValue,
  TimeSeriesPoint,
  Variant,
} from "../../../../../gen/witbooking/backoffice/orchestrator/experiment/v1/experiment_api_pb";

/** Epoch-ms `0` is the wire's "unset" convention; every other value becomes a real `Date`. */
function epochMsToDateOrNull(ms: number): Date | null {
  return ms > 0 ? new Date(ms) : null;
}

/** `null`/`undefined` map to `0` ("unset") on the wire; any `Date` becomes its epoch-ms value. */
function dateToEpochMs(date: Date | null | undefined): number {
  return date ? date.getTime() : 0;
}

/** Empty string is the wire's "unset" convention for optional string fields. */
function emptyToNull(value: string): string | null {
  return value === "" ? null : value;
}

/** `null`/`undefined` map to `""` ("unset") on the wire. */
function nullToEmpty(value: string | null | undefined): string {
  return value ?? "";
}

export class ExperimentMapper {
  static toDomain(proto: Experiment): ExperimentEntity {
    const obj = proto.toObject();
    const variants = proto
      .getVariantsList()
      .map(variant => ExperimentMapper.variantToDomain(variant));

    return {
      id: obj.id,
      ticker: obj.ticker,
      description: emptyToNull(obj.description),
      // Proto enums are proto-verified to match the domain enum's integer values 1:1 (Business
      // Rule 3) — a direct numeric cast, no lookup table, matching the legacy convention.
      status: obj.status as ExperimentStatus,
      targetPath: obj.targetPath,
      // `startedAt` is required (non-nullable) by `ExperimentSchema`, so the result is asserted
      // non-null after conversion — the backend guarantees every experiment read via
      // getExperiment/getAllExperiments/listExperiments already has a `started_at` (Business Rule 1).
      startedAt: epochMsToDateOrNull(obj.startedAt)!,
      estimatedEndDate: epochMsToDateOrNull(obj.estimatedEndDate),
      mde: obj.mde,
      minDays: obj.minDays,
      variants: [variants[0], variants[1]],
      createdAt: epochMsToDateOrNull(obj.createdAt),
      updatedAt: epochMsToDateOrNull(obj.updatedAt),
      deletedAt: epochMsToDateOrNull(obj.deletedAt),
    };
  }

  static toDomainArray(protos: Experiment[]): ExperimentEntity[] {
    return protos.map(proto => ExperimentMapper.toDomain(proto));
  }

  static toProto(entity: ExperimentEntity): Experiment {
    const experiment = new Experiment();
    experiment.setId(entity.id);
    experiment.setTicker(entity.ticker);
    experiment.setDescription(nullToEmpty(entity.description));
    experiment.setStatus(entity.status);
    experiment.setTargetPath(entity.targetPath);
    experiment.setStartedAt(dateToEpochMs(entity.startedAt));
    experiment.setEstimatedEndDate(dateToEpochMs(entity.estimatedEndDate));
    experiment.setMde(entity.mde);
    experiment.setMinDays(entity.minDays);
    experiment.setVariantsList(
      entity.variants.map(variant => ExperimentMapper.variantToProto(variant))
    );
    experiment.setCreatedAt(dateToEpochMs(entity.createdAt));
    experiment.setUpdatedAt(dateToEpochMs(entity.updatedAt));
    experiment.setDeletedAt(dateToEpochMs(entity.deletedAt));
    return experiment;
  }

  static createInputToProto(id: string, data: ExperimentCreateInput): Experiment {
    const experiment = new Experiment();
    experiment.setId(id);
    experiment.setTicker(data.ticker);
    experiment.setDescription(nullToEmpty(data.description));
    // The backend's own creation rule ("V12 - allowed initial status") requires the incoming
    // status to already be DRAFT or RUNNING - it is never defaulted server-side, so leaving this
    // unset (proto default: EXPERIMENT_STATUS_UNSPECIFIED) is rejected outright. Every new
    // experiment starts as DRAFT, matching `createEmptyExperiment`'s own domain default.
    experiment.setStatus(ExperimentStatus.EXPERIMENT_STATUS_DRAFT);
    // `targetPath` carries a Zod `.default("/")` on `ExperimentBaseSchema`, so `ExperimentCreateInput`
    // (a `z.input` type) types it as optional — applying the same default here mirrors what
    // `ExperimentCreateInputSchema.parse` would fill in at the form/hook layer.
    experiment.setTargetPath(data.targetPath ?? "/");
    experiment.setStartedAt(dateToEpochMs(data.startedAt));
    experiment.setEstimatedEndDate(dateToEpochMs(data.estimatedEndDate));
    // Same `z.input` reasoning as `targetPath` above: both carry a Zod `.default()`, so the domain
    // default is applied here rather than relying on the proto's own 0-is-unset fallback — the user
    // sees 20/14 in the form, so 20/14 is what the backend must be told.
    experiment.setMde(data.mde ?? 20);
    experiment.setMinDays(data.minDays ?? 14);
    experiment.setVariantsList(
      data.variants.map(variant => ExperimentMapper.variantToProto(variant))
    );
    // createdAt/updatedAt/deletedAt are server/lifecycle-assigned and correctly omitted from
    // `ExperimentCreateInput`.
    return experiment;
  }

  static variantToDomain(proto: Variant): VariantEntity {
    const obj = proto.toObject();
    return {
      id: obj.id,
      ticker: obj.ticker,
      // Direct numeric cast (Business Rule 3) — VariantType's integer values are proto-verified to
      // match 1:1.
      type: obj.type as VariantType,
      scenarioId: emptyToNull(obj.scenarioId),
      url: emptyToNull(obj.url),
      trafficSplit: obj.trafficSplit,
      scenarioTicker: emptyToNull(obj.scenarioTicker),
      role: obj.role as VariantRole,
      isEngineDefault: obj.isEngineDefault,
    };
  }

  // Accepts `VariantInput` (rather than the stricter `VariantEntity`) so it can be reused for
  // `ExperimentCreateInput.variants`, whose entries are unparsed `z.input` shapes with the same
  // Zod defaults `VariantSchema` declares — applied explicitly here, mirroring what
  // `VariantSchema.parse` would fill in.
  static variantToProto(entity: VariantInput | VariantEntity): Variant {
    const variant = new Variant();
    variant.setId(entity.id);
    variant.setTicker(entity.ticker);
    variant.setType(entity.type ?? VariantType.VARIANT_TYPE_SCENARIO);
    variant.setScenarioId(nullToEmpty(entity.scenarioId));
    variant.setUrl(nullToEmpty(entity.url));
    variant.setTrafficSplit(entity.trafficSplit ?? 50);
    variant.setScenarioTicker(nullToEmpty(entity.scenarioTicker));
    variant.setRole(entity.role ?? VariantRole.VARIANT_ROLE_UNSPECIFIED);
    variant.setIsEngineDefault(entity.isEngineDefault ?? false);
    return variant;
  }

  static toListItemDomain(proto: ExperimentListItemProto): ExperimentListItem {
    const obj = proto.toObject();
    const experimentProto = proto.getExperiment();
    if (!experimentProto) {
      throw new Error("Unexpected ExperimentListItem response with no experiment set");
    }

    return {
      experiment: ExperimentMapper.toDomain(experimentProto),
      // No business calculation (Business Rule 4/AC5): every field below is a direct, pass-through
      // copy of the value the backend already computed.
      lift: obj.lift,
      hasLift: obj.hasLift,
      confidence: obj.confidence,
      isReliable: obj.isReliable,
      activeDays: obj.activeDays,
      liftTooltipKey: obj.liftTooltipKey,
      sampleProgress: obj.sampleProgress,
    };
  }

  static toResultsDomain(response: GetExperimentResultsResponse): ExperimentResults {
    const experimentProto = response.getExperiment();
    const scenarioAProto = response.getScenarioA();
    const scenarioBProto = response.getScenarioB();
    if (!experimentProto || !scenarioAProto || !scenarioBProto) {
      throw new Error("Unexpected response fetching Experiment results");
    }

    return {
      experiment: ExperimentMapper.toDomain(experimentProto),
      verdict: response.getVerdict() as Verdict,
      scenarioA: ExperimentMapper.toScenarioMetricsDomain(scenarioAProto),
      scenarioB: ExperimentMapper.toScenarioMetricsDomain(scenarioBProto),
      table: response.getTableList().map(entry => ExperimentMapper.toMetricComparisonDomain(entry)),
      series: response
        .getSeriesList()
        .map(point => ExperimentMapper.toTimeSeriesPointDomain(point)),
      activeDays: response.getActiveDays(),
      remainingDays: response.getRemainingDays(),
      hasRemainingDays: response.getHasRemainingDays(),
      minBookingsReached: response.getMinBookingsReached(),
      minBookingsThreshold: response.getMinBookingsThreshold(),
      partial: response.getPartial(),
      warnings: response.getWarningsList(),
      // Every field below is a verbatim pass-through of a number the backend already computed —
      // no rescaling, no re-derivation (see this change's design.md, Decisions 1 and 2).
      confidence: response.getConfidence(),
      pValue: response.getPValue(),
      srmPValue: response.getSrmPValue(),
      srmAlert: response.getSrmAlert(),
      expectedShareA: response.getExpectedShareA(),
      observedShareA: response.getObservedShareA(),
      requiredVisitors: response.getRequiredVisitors(),
      sampleProgress: response.getSampleProgress(),
      estimatedDaysLeft: response.getEstimatedDaysLeft(),
      hasEstimatedDaysLeft: response.getHasEstimatedDaysLeft(),
      mde: response.getMde(),
      minDays: response.getMinDays(),
      dataUntil: emptyToNull(response.getDataUntil()),
      source: emptyToNull(response.getSource()),
      isReliable: response.getIsReliable(),
      segments: response
        .getSegmentsList()
        .map(segment => ExperimentMapper.toSegmentMetricsDomain(segment)),
      pauses: response.getPausesList().map(pause => ExperimentMapper.toPausePeriodDomain(pause)),
      lift: response.getLift(),
      liftCiLow: response.getLiftCiLow(),
      liftCiHigh: response.getLiftCiHigh(),
      hasLiftCi: response.getHasLiftCi(),
    };
  }

  static toSegmentMetricsDomain(proto: SegmentMetrics): SegmentMetricsEntity {
    const scenarioA = proto.getScenarioA();
    const scenarioB = proto.getScenarioB();
    if (!scenarioA || !scenarioB) {
      throw new Error("Unexpected SegmentMetrics with no scenario set");
    }
    return {
      dimension: proto.getDimension(),
      value: proto.getValue(),
      scenarioA: ExperimentMapper.toScenarioMetricsDomain(scenarioA),
      scenarioB: ExperimentMapper.toScenarioMetricsDomain(scenarioB),
      confidence: proto.getConfidence(),
      liftPct: proto.getLiftPct(),
      smallSample: proto.getSmallSample(),
    };
  }

  static toPausePeriodDomain(proto: PausePeriod): PausePeriodEntity {
    return {
      // `from` is always a real instant on a pause the backend chose to send; `to === 0` is the
      // wire's "still paused" convention, which `epochMsToDateOrNull` already turns into null.
      from: epochMsToDateOrNull(proto.getFrom())!,
      to: epochMsToDateOrNull(proto.getTo()),
    };
  }

  static toScenarioMetricsDomain(proto: ScenarioMetrics): ScenarioMetricsEntity {
    const obj = proto.toObject();
    return {
      variantId: obj.variantId,
      variantTicker: obj.variantTicker,
      role: obj.role as VariantRole,
      visits: obj.visits,
      bookings: obj.bookings,
      conversionRate: obj.conversionRate,
      revenueTotal: obj.revenueTotal,
      revenuePerVisit: obj.revenuePerVisit,
      adr: obj.adr,
      avgBookingValue: obj.avgBookingValue,
      abandonmentRate: obj.abandonmentRate,
      currency: obj.currency,
      validVisitors: obj.validVisitors,
      excludedVisitors: obj.excludedVisitors,
      ciLow: obj.ciLow,
      ciHigh: obj.ciHigh,
      botSuspectVisitors: obj.botSuspectVisitors,
    };
  }

  static toMetricComparisonDomain(proto: MetricComparison): MetricComparisonEntity {
    const obj = proto.toObject();
    return {
      metricKey: obj.metricKey,
      valueA: obj.valueA,
      valueB: obj.valueB,
      winner: obj.winner as MetricComparisonEntity["winner"],
      relativeDiff: obj.relativeDiff,
      diffUnit: obj.diffUnit as DiffUnit,
      higherIsBetter: obj.higherIsBetter,
    };
  }

  static toTimeSeriesPointDomain(proto: TimeSeriesPoint): TimeSeriesPointEntity {
    const metrics: Record<string, TimeSeriesMetricValueEntity> = {};
    proto.getMetricsMap().forEach((value, key) => {
      metrics[key] = ExperimentMapper.toTimeSeriesMetricValueDomain(value);
    });

    return {
      // `bucketStart` is required (non-nullable) by `TimeSeriesPointSchema`, matching
      // `Experiment.startedAt`'s own guarantee — a real backend response always has one.
      bucketStart: epochMsToDateOrNull(proto.getBucketStart())!,
      labelKey: proto.getLabelKey(),
      metrics,
      // Direct numeric cast (Business Rule 3) — Granularity's integer values are proto-verified.
      granularity: proto.getGranularity() as Granularity,
      bucketEnd: epochMsToDateOrNull(proto.getBucketEnd()),
    };
  }

  static toTimeSeriesMetricValueDomain(proto: TimeSeriesMetricValue): TimeSeriesMetricValueEntity {
    return {
      a: proto.getA()?.getValue() ?? null,
      b: proto.getB()?.getValue() ?? null,
      // Unset wrappers stay null: the chart reads "no band to draw", which is the correct meaning
      // for every metric other than conversion_rate.
      ciLowA: proto.getCiLowA()?.getValue() ?? null,
      ciHighA: proto.getCiHighA()?.getValue() ?? null,
      ciLowB: proto.getCiLowB()?.getValue() ?? null,
      ciHighB: proto.getCiHighB()?.getValue() ?? null,
    };
  }
}
